import { createFileRoute, Link } from "@tanstack/react-router";
import { MessageSquare, Plus } from "lucide-react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  daysLeft,
  formatDay,
  type OrgListState,
  RestoreButton,
  useOrgList,
} from "@/features/offboarding";
import type { OrgSummary } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Every client, and which of them need The Motherhood.
 *
 * This is deliberately the only place to look: rather than a separate review
 * queue, the state of each client's review sits on their row, so one page
 * answers both "who are our clients" and "what needs me today".
 */
type Search = { state?: OrgListState };

const LISTS: { state: OrgListState; label: string }[] = [
  { state: "active", label: "Active" },
  { state: "archived", label: "Archived" },
  { state: "removed", label: "Removed" },
];

export const Route = createFileRoute("/_authenticated/admin/orgs/")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    state: search.state === "archived" || search.state === "removed" ? search.state : undefined,
  }),
  component: OrgsPage,
});

function OrgsPage() {
  const state = Route.useSearch().state ?? "active";
  const { data, isPending, isError, refetch } = useOrgList(state);

  const needingAttention = (data?.items ?? []).filter((o) => o.openCommentCount > 0).length;

  return (
    <>
      <PageHeader
        eyebrow="The Motherhood"
        title="Organizations"
        description={
          needingAttention > 0
            ? `${needingAttention} ${needingAttention === 1 ? "client has" : "clients have"} sent feedback that is waiting on you.`
            : "Every client you run an EGC programme for."
        }
        actions={
          <Button asChild>
            <Link to="/admin/orgs/new">
              <Plus /> New organization
            </Link>
          </Button>
        }
      />

      <nav
        aria-label="Organization lists"
        className="mb-6 flex w-fit gap-1 rounded-full bg-muted p-1"
      >
        {LISTS.map((list) => (
          <Link
            key={list.state}
            to="/admin/orgs"
            search={list.state === "active" ? {} : { state: list.state }}
            aria-current={state === list.state ? "page" : undefined}
            className={cn(
              "rounded-full px-4 py-1.5 text-sm transition-colors",
              state === list.state
                ? "bg-card font-medium text-foreground shadow-xs"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {list.label}
          </Link>
        ))}
      </nav>

      {isPending ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : state !== "active" ? (
        <ClosedOrgs state={state} items={data.items} />
      ) : data.items.length === 0 ? (
        <EmptyState
          title="No organizations yet"
          description="Set one up and we will invite their social media manager to get started."
          action={
            <Button asChild>
              <Link to="/admin/orgs/new">
                <Plus /> Set up the first one
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Brand setup</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((org) => (
                <TableRow key={org.id} className="relative cursor-pointer hover:bg-accent/50">
                  <TableCell className="font-medium text-foreground">
                    {/* The whole row is the target, so the click area matches the
                        way a table reads. */}
                    <Link
                      to="/admin/orgs/$orgId"
                      params={{ orgId: org.id }}
                      className="after:absolute after:inset-0 after:content-['']"
                    >
                      {org.name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {org.industry ?? "Not set"}
                  </TableCell>
                  <TableCell>
                    <OrgStatusBadge status={org.status} />
                  </TableCell>
                  <TableCell>
                    <ReviewState org={org} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

/** Where the client sits overall: still being set up, live, or paused. */
function OrgStatusBadge({ status }: { status: OrgSummary["status"] }) {
  switch (status) {
    case "active":
      return <Badge variant="success">Active</Badge>;
    case "paused":
      return <Badge variant="outline">Paused</Badge>;
    default:
      return <Badge>Setting up</Badge>;
  }
}

/** What this client's brand setup is waiting on, if anything. */
function ReviewState({ org }: { org: OrgSummary }) {
  if (org.openCommentCount > 0) {
    return (
      <Badge variant="destructive">
        <MessageSquare /> {org.openCommentCount} waiting on you
      </Badge>
    );
  }
  switch (org.approvalStatus) {
    case "approved":
      return <Badge variant="success">Approved</Badge>;
    case "changes_requested":
      return <Badge>Changes made, not signed off</Badge>;
    case "pending":
      return <Badge>Waiting on the client</Badge>;
    default:
      return <span className="text-muted-foreground text-sm">Not started</span>;
  }
}

/** Archived or removed clients: when, how long is left, and a way back. */
function ClosedOrgs({ state, items }: { state: OrgListState; items: OrgSummary[] }) {
  if (items.length === 0) {
    return (
      <EmptyState
        title={state === "archived" ? "No archived organizations" : "No removed organizations"}
        description={
          state === "archived"
            ? "An archived org keeps its data and can be restored. Archive one from its page."
            : "A removed org is deleted 90 days after removal. Until then it waits here."
        }
      />
    );
  }
  const removed = state === "removed";
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <Table aria-label={removed ? "Removed organizations" : "Archived organizations"}>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>{removed ? "Removed" : "Archived"}</TableHead>
            {removed ? <TableHead>Deleted from the platform</TableHead> : null}
            {removed ? <TableHead>Downloads</TableHead> : null}
            <TableHead className="sr-only">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((org) => {
            const left = org.purgeAfter ? daysLeft(org.purgeAfter) : 0;
            return (
              <TableRow key={org.id} className="relative hover:bg-accent/50">
                <TableCell className="font-medium text-foreground">
                  <Link
                    to="/admin/orgs/$orgId"
                    params={{ orgId: org.id }}
                    className="after:absolute after:inset-0 after:content-['']"
                  >
                    {org.name}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDay((removed ? org.removedAt : org.archivedAt) ?? org.updatedAt)}
                </TableCell>
                {removed ? (
                  <TableCell>
                    {org.purgeAfter ? formatDay(org.purgeAfter) : ""}
                    <span className="block text-muted-foreground text-xs">
                      {left} {left === 1 ? "day" : "days"} left
                    </span>
                  </TableCell>
                ) : null}
                {removed ? (
                  <TableCell>
                    {(org.exportProblems ?? 0) > 0 ? (
                      <Badge variant="destructive">
                        {org.exportProblems} {org.exportProblems === 1 ? "needs" : "need"} you
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-sm">No problems</span>
                    )}
                  </TableCell>
                ) : null}
                <TableCell className="text-right">
                  <RestoreButton org={org} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
